/**
 * Unified selectable expansions / modules / addons per game.
 * Merges shared expansion catalog, manual addons, and game-specific modules.
 */

import { getExpansionsForGame } from './expansions';
import { gameManuals } from './gameManuals';
import {
  carcassonneModules,
  getDefaultEnabledModuleIds,
} from '../games/carcassonne/data/modules';
import type { GamePrefs } from '../state/gamePrefs';
import { createDefaultGamePrefs } from '../state/gamePrefs';

export type SelectableContentKind = 'expansion' | 'module' | 'addon';

export type SelectableContentOption = {
  id: string;
  gameId: string;
  title: string;
  description?: string;
  kind: SelectableContentKind;
  /** Cannot be turned off (e.g. Carcassonne base). */
  alwaysOn?: boolean;
  defaultSelected?: boolean;
  /** When true, selection is stored in selectedModuleIds; else selectedExpansionIds. */
  storeAsModule?: boolean;
};

const ODIN_NORWEGIANS: SelectableContentOption = {
  id: 'norwegians',
  gameId: 'uczta-dla-odyna',
  title: 'Norwegowie',
  description: 'Dodatek Norwegowie (zwierzęta, kafle, zasady).',
  kind: 'module',
  defaultSelected: true,
  storeAsModule: true,
};

function optionsFromManualAddons(gameId: string): SelectableContentOption[] {
  const addons = gameManuals[gameId]?.addons ?? [];
  return addons.map((addon) => ({
    id: addon.id,
    gameId,
    title: addon.name,
    description: addon.description,
    kind: 'addon' as const,
    defaultSelected: false,
    storeAsModule: false,
  }));
}

function optionsFromCatalog(gameId: string): SelectableContentOption[] {
  return getExpansionsForGame(gameId).map((expansion) => ({
    id: expansion.id,
    gameId,
    title: expansion.shortTitle ?? expansion.title,
    description: expansion.description,
    kind: 'expansion' as const,
    defaultSelected: Boolean(expansion.enabledByDefault),
    storeAsModule: false,
  }));
}

function carcassonneOptions(): SelectableContentOption[] {
  return carcassonneModules
    .filter((module) => module.status === 'ready')
    .map((module) => ({
      id: module.id,
      gameId: 'carcassonne',
      title: module.name,
      description: module.description,
      kind: 'module' as const,
      alwaysOn: Boolean(module.alwaysOn),
      defaultSelected: Boolean(module.defaultEnabled),
      storeAsModule: true,
    }));
}

/**
 * Deduped selectable options for a game.
 * Catalog expansions win over identically-id'd manual addons.
 */
export function getSelectableContentForGame(
  gameId: string
): SelectableContentOption[] {
  if (gameId === 'carcassonne') {
    return carcassonneOptions();
  }

  if (gameId === 'uczta-dla-odyna') {
    return [ODIN_NORWEGIANS];
  }

  const byId = new Map<string, SelectableContentOption>();
  for (const option of optionsFromManualAddons(gameId)) {
    byId.set(option.id, option);
  }
  for (const option of optionsFromCatalog(gameId)) {
    byId.set(option.id, option);
  }

  // Ark Nova catalog uses ark-nova-marine-worlds; drop duplicate manual addon if present.
  return Array.from(byId.values());
}

export function getAlwaysOnIds(gameId: string): string[] {
  return getSelectableContentForGame(gameId)
    .filter((option) => option.alwaysOn)
    .map((option) => option.id);
}

export function getDefaultSelectedIds(gameId: string): {
  selectedExpansionIds: string[];
  selectedModuleIds: string[];
} {
  if (gameId === 'carcassonne') {
    return {
      selectedExpansionIds: [],
      selectedModuleIds: [...getDefaultEnabledModuleIds()],
    };
  }

  const options = getSelectableContentForGame(gameId);
  const selectedExpansionIds: string[] = [];
  const selectedModuleIds: string[] = [];
  for (const option of options) {
    if (!option.defaultSelected && !option.alwaysOn) continue;
    if (option.storeAsModule) selectedModuleIds.push(option.id);
    else selectedExpansionIds.push(option.id);
  }
  return { selectedExpansionIds, selectedModuleIds };
}

/** Fill defaults when a game has never been configured. */
export function withDefaultSelection(
  gameId: string,
  prefs: GamePrefs | undefined
): GamePrefs {
  const base = prefs ?? createDefaultGamePrefs();
  const hasAnySelection =
    base.selectedExpansionIds.length > 0 || base.selectedModuleIds.length > 0;

  let next = base;
  if (hasAnySelection) {
    const alwaysOn = getAlwaysOnIds(gameId);
    if (alwaysOn.length > 0) {
      const modules = new Set([...base.selectedModuleIds, ...alwaysOn]);
      next = {
        ...base,
        selectedModuleIds: Array.from(modules),
      };
    }
  } else {
    const defaults = getDefaultSelectedIds(gameId);
    next = {
      ...base,
      selectedExpansionIds: defaults.selectedExpansionIds,
      selectedModuleIds: defaults.selectedModuleIds,
    };
  }

  // Vinhos: default active variant when unset.
  if (gameId === 'vinhos-deluxe' && !next.activeVariantId) {
    next = { ...next, activeVariantId: 'special-vintage-2016' };
  }
  return next;
}

export function isOptionSelected(
  option: SelectableContentOption,
  prefs: GamePrefs
): boolean {
  if (option.alwaysOn) return true;
  if (option.storeAsModule) {
    return prefs.selectedModuleIds.includes(option.id);
  }
  return prefs.selectedExpansionIds.includes(option.id);
}
